<template>
  <v-container fluid>
    <v-layout column>
      <v-layout row align-center>
        <!-- row of filters and button -->
        <v-layout column>
          <v-layout row>
            <v-flex shrink class="mr-2">
              <v-select
                label="Shift Status"
                :items="statusFilters"
                v-model="selectedStatusFilter"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="Position"
                multiple
                chips
                :items="positionFilters"
                item-text="name"
                item-value="id"
                v-model="selectedPositionFilter"
                :loading="positionsLoading"
                :disabled="positionsLoading"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-autocomplete
                label="Location"
                multiple
                chips
                :items="locationFilters"
                item-text="name"
                item-value="id"
                v-model="selectedLocationFilter"
                :loading="locationsLoading"
                :disabled="locationsLoading"
                clearable
              ></v-autocomplete>
              <v-switch label="My Locations" v-model="managedLocations">
              </v-switch>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="State"
                :items="stateFilterOptions"
                v-model="selectedStateFilter"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-autocomplete
                label="Employee"
                :items="employeeFilters"
                item-text="name"
                item-value="id"
                v-model="selectedEmployeeFilter"
                :loading="employeesLoading"
                :disabled="employeesLoading"
                clearable
              ></v-autocomplete>
              <v-switch label="My Employees" v-model="managedEmployees">
              </v-switch>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="Bonus"
                :items="bonusFilterOptions"
                v-model="selectedbonusFilter"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-switch
                v-model="selectedTradeFilter"
                label="View Shift Trades"
              ></v-switch>
            </v-flex>
          </v-layout>
        </v-layout>
        <v-layout row>
          <v-btn @click="reGetRecords" color="primary">
            Update
          </v-btn>
        </v-layout>
      </v-layout>

      <v-divider />

      <FilterPresets
        :currentQuery="formattedQuery"
        @updateFilters="updateFilters"
      />

      <v-layout justify-space-between align-center class="my-2">
        <v-flex shrink>
          <a href="#" @click.prevent="goToList">Switch To List View</a>
          <SetDefaultViewButton view="month" class="ml-3" />
        </v-flex>
        <v-flex shrink>
          <v-btn :to="`/events`" color="info">
            View All Events
          </v-btn>
          <v-btn :to="`/event/new`" color="success">
            <v-icon left>add</v-icon>
            Create Event
          </v-btn>
          <v-btn :to="`/service/new/`" color="success">
            <v-icon left>add</v-icon>
            Create Service Request
          </v-btn>
        </v-flex>
      </v-layout>

      <v-layout row justify-space-between align-center class="mb-2">
        <v-flex xs3>
          <h2 class="headline mb-2">{{ currentlyViewedCalendar }}</h2>
        </v-flex>

        <v-flex xs3>
          <v-layout row justify-space-around align-center>
            <v-flex shrink>
              <v-btn small @click="goMonthBack">
                <v-icon small>
                  keyboard_arrow_left
                </v-icon>
              </v-btn>
            </v-flex>
            <v-flex shrink>
              <v-btn @click="goToToday">
                Today
              </v-btn>
            </v-flex>
            <v-flex shrink>
              <v-btn small @click="goMonthForward">
                <v-icon small>
                  keyboard_arrow_right
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs3>
          <v-spacer />
        </v-flex>
      </v-layout>

      <v-flex v-if="shiftQueryPending">
        <v-progress-linear indeterminate> </v-progress-linear>
      </v-flex>

      <v-flex v-else>
        <v-sheet height="700">
          <v-calendar
            ref="calendar"
            class="vss-calendar"
            v-model="startDate"
            :end="endDate"
            :now="today"
            type="month"
            v-on:click:date="changeToDayView"
          >
            <template slot="day" slot-scope="{ date }">
              <span class="shift-overflow-count">
                <v-chip
                  small
                  v-if="shiftsOnDate(date) > 1"
                  class="grey--text text--darken-2"
                  @click="changeToDayView(date)"
                >
                  {{ shiftsOnDate(date) }} shifts
                </v-chip>
              </span>

              <span v-if="date === '2022-06-29'" class="day-event">
                <v-chip
                  small
                  color="orange"
                  text-color="white"
                  @click="goToEvent()"
                >
                  <v-avatar>
                    <v-icon>event</v-icon>
                  </v-avatar>
                  {{ date | moment("DD") }}
                </v-chip>
              </span>

              <template v-for="shift in shifts">
                <div v-if="shiftIsOnDay(shift, date)">
                  <shiftOnMonth :shift="shift" :user="user" />
                </div>
              </template>
            </template>
          </v-calendar>
        </v-sheet>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import shiftOnMonth from "@/components/calendar/shiftOnMonth.vue";
import collectionApi from "@/api/collections";
import FilterPresets from "@/components/calendar/filter-presets.vue";
import SetDefaultViewButton from "@/components/calendar/set-default-view-button.vue";

import { mapState, mapGetters } from "vuex";

export default {
  components: {
    shiftOnMonth,
    FilterPresets,
    SetDefaultViewButton,
  },
  created() {
    this.setUp();
  },
  watch: {
    selectedEmployeeFilter: {
      handler(value) {
        if (value) {
          this.managedEmployees = false;
        }
      }
    },
    managedEmployees: {
      handler(value) {
        if (value) {
          this.selectedEmployeeFilter = null;
        }
      }
    },
    selectedLocationFilter: {
      handler(value) {
        if (value) {
          this.managedLocations = false;
        }
      }
    },
    managedLocations: {
      handler(value) {
        if (value) {
          this.selectedLocationFilter = null;
        }
      }
    }
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    currentlyViewedCalendar: function() {
      return this.$moment(this.startDate).format("MMMM, YYYY");
    },
    formattedQuery: function() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        managedLocations: this.managedLocations,
        managedEmployees: this.managedEmployees,
        statusFilter: this.selectedStatusFilter,
        state: this.selectedStateFilter,
        positionFilter: this.selectedPositionFilter,
        locationFilter: this.selectedLocationFilter,
        employeeFilter: this.selectedEmployeeFilter,
        bonusFilter: this.selectedbonusFilter,
        tradeFilter: this.selectedTradeFilter
      };
    },
    firstVisibleDate: function() {
      return this.$refs.calendar.$children[0].days[0].date;
    },
    lastVisibleDate: function() {
      return this.$refs.calendar.$children[0].days.slice().pop().date;
    }
  },
  methods: {
    goToEvent() {
      this.$router.push({ name: "eventDetail", params: { eventId: 3 } });
    },
    updateFilters(e) {
      this.selectedStatusFilter = e["statusFilter"];
      this.selectedStateFilter = e["state"];
      this.selectedPositionFilter = e["positionFilter"];
      this.selectedLocationFilter = e["locationFilter"];
      this.selectedEmployeeFilter = e["employeeFilter"];
      this.selectedbonusFilter = e["bonusFilter"];
      this.selectedTradeFilter = e["tradeFilter"];

      this.managedEmployees = e["managedEmployees"];
      this.managedLocations = e["managedLocations"];

      this.reGetRecords();
    },

    goToList: function() {
      this.$router.push({
        name: "calendar",
        query: this.formattedQuery
      });
    },

    goToToday: function() {
      this.startDate = this.beginningOfCurrentMonth;
      this.endDate = this.endOfCurrentMonth;
      this.reGetRecords();
    },
    goMonthBack: function() {
      let newStart = this.$moment(this.startDate)
        .subtract(1, "month")
        .format("YYYY-MM-DD");
      let newEnd = this.$moment(this.endDate)
        .subtract(1, "month")
        .format("YYYY-MM-DD");
      this.startDate = newStart;
      this.endDate = newEnd;
      this.reGetRecords();
      this.$refs.calendar.prev();
    },
    goMonthForward: function() {
      let newStart = this.$moment(this.startDate)
        .add(1, "month")
        .format("YYYY-MM-DD");
      let newEnd = this.$moment(this.endDate)
        .add(1, "month")
        .format("YYYY-MM-DD");
      this.startDate = newStart;
      this.endDate = newEnd;
      this.reGetRecords();
      this.$refs.calendar.next();
    },

    dateFormatted: function(date) {
      return this.$moment(date).format("YYYY-MM-DD");
    },

    changeToDayView: function(date) {
      let query = {
        ...this.formattedQuery,
        startDate: date.date,
        endDate: date.date
      };
      this.$router.push({
        name: "calendar",
        query: query
      });
    },

    reGetRecords: function() {
      this.$router.push({
        name: "month",
        query: this.formattedQuery
      });
    },

    getShifts: function() {
      // Need to widen the search (but not the query in the URL) to account
      // for visible dates outside this month in the calendar view
      let widerStart = this.$moment(this.formattedQuery.startDate)
        .subtract(6, "days")
        .format("YYYY-MM-DD");
      let widerEnd = this.$moment(this.formattedQuery.endDate)
        .add(6, "days")
        .format("YYYY-MM-DD");

      let params = {
        ...this.formattedQuery,
        startDate: widerStart,
        endDate: widerEnd
      };

      this.shiftQueryPending = true;
      this.$store
        .dispatch("shifts/getList", params)
        .then(res => {
          this.queryTotal = res.total;
          this.shifts = res.shifts;
        })
        .finally(res => {
          this.shiftQueryPending = false;
        });
    },
    setUp: function() {
      this.startDate = this.$route.query.startDate || this.startDate;
      this.endDate = this.$route.query.endDate || this.endDate;
      this.selectedStatusFilter =
        this.$route.query.statusFilter || this.selectedStatusFilter;
      this.selectedStateFilter =
        this.$route.query.state || this.selectedStateFilter;
      this.selectedPositionFilter =
        (this.$route.query.positionFilter
          ? this.$route.query.positionFilter.map(id => parseInt(id))
          : null) || this.selectedPositionFilter;
      this.selectedLocationFilter =
        (this.$route.query.locationFilter
          ? this.$route.query.locationFilter.map(id => parseInt(id))
          : null) || this.selectedLocationFilter;
      this.selectedEmployeeFilter =
        parseInt(this.$route.query.employeeFilter) ||
        this.selectedEmployeeFilter;
      this.selectedbonusFilter =
        this.$route.query.bonusFilter || this.selectedbonusFilter;
      this.selectedTradeFilter =
        this.$route.query.tradeFilter || this.selectedTradeFilter;

      // this is bonkers:
      if (typeof this.$route.query.managedLocations == "undefined") {
        this.managedLocations = true;
      } else {
        this.managedLocations =
          this.$route.query.managedLocations === "true" ||
          (this.$route.query.managedLocations &&
            this.$route.query.managedLocations !== "false")
            ? true
            : false;
      }
      this.managedEmployees =
        this.$route.query.managedEmployees || this.managedEmployees;

      this.$router.push({
        name: "month",
        query: this.formattedQuery
      });

      this.getShifts();

      this.positionsLoading = true;
      collectionApi.getPositions(
        positions => {
          this.positionFilters = positions;
          this.positionsLoading = false;
        },
        error => {
          console.log(error);
          this.positionsLoading = false;
        }
      );

      this.locationsLoading = true;
      collectionApi.getLocations(
        locations => {
          this.locationFilters = locations;
          this.locationsLoading = false;
        },
        error => {
          console.log(error);
          this.locationsLoading = false;
        }
      );

      this.employeesLoading = true;
      collectionApi.getEmployees(
        employees => {
          this.employeeFilters = employees;
          this.employeesLoading = false;
        },
        error => {
          console.log(error);
          this.employeesLoading = false;
        }
      );
    },
    shiftIsOnDay: function(shift, day) {
      return this.$moment(shift.startDatetime).isSame(day, "day");
    },
    shiftsOnDate: function(date) {
      const amount = this.shifts.filter(s => {
        return this.$moment(s.startDatetime).format("YYYY-MM-DD") == date;
      }).length;
      return amount;
    }
  },

  data: function() {
    return {
      today: this.$moment().format("YYYY-MM-DD"),
      startDateMenu: false,
      endDateMenu: false,
      beginningOfCurrentMonth: this.$moment()
        .startOf("month")
        .format("YYYY-MM-DD"),
      startDate: this.$moment()
        .startOf("month")
        .format("YYYY-MM-DD"),
      endOfCurrentMonth: this.$moment()
        .endOf("month")
        .format("YYYY-MM-DD"),
      endDate: this.$moment()
        .endOf("month")
        .format("YYYY-MM-DD"),
      shifts: [],
      shiftQueryPending: false,
      positionsLoading: false,
      locationsLoading: false,
      employeesLoading: false,
      statusFilters: [
        {
          text: "Open Requests",
          value: "available"
        },
        {
          text: "Assigned",
          value: "assigned"
        },
        {
          text: "Pending Approval",
          value: "pending"
        },
        {
          text: "Employee was Absent",
          value: "absent"
        }
      ],
      selectedStatusFilter: null,

      stateFilterOptions: collectionApi.getStates(),
      selectedStateFilter: null,

      positionFilters: [],
      selectedPositionFilter: [],

      locationFilters: [],
      selectedLocationFilter: [],

      employeeFilters: [],
      selectedEmployeeFilter: null,

      managedLocations: null,
      managedEmployees: null,

      bonusFilterOptions: [
        {
          text: "Straight Bonus",
          value: "straightBonus"
        },
        {
          text: "Call in Bonus",
          value: "callInBonus"
        },
        {
          text: "Travel Bonus",
          value: "travelBonus"
        }
      ],
      selectedbonusFilter: null,

      selectedTradeFilter: null,

      // selectedMultiFilter: null,
      // selectedMultiFilterClient: null,
      // selectedMultiFilterEmployee: null,
      multiFilterOptions: [
        {
          text: "By Client",
          value: "clients"
        },
        {
          text: "By Employee",
          value: "employees"
        }
      ]
    };
  }
};
</script>

<style scoped>
#dashboard-container {
}

.my-event {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 2px;
  border: 1px solid #1867c0;
  font-size: 12px;
  padding: 3px;
  cursor: pointer;
  margin-bottom: 1px;
  left: 4px;
  margin-right: 8px;
  position: relative;
}

.my-event.with-time {
  position: absolute;
  right: 4px;
  margin-right: 0px;
}

.pending-approval {
  opacity: 0.3;
  border: 1px dashed black !important;
  border-radius: 4px;
  padding: 4px;
}

.shift-overflow-count {
  position: absolute;
  top: 0px;
  right: 0px;
}
.day-event {
  position: absolute;
  top: 0px;
  left: 0px;
  cursor: pointer;
}
</style>

<style>
.vss-calendar.v-calendar.timeframe-week .v-calendar-daily__body {
  display: none !important;
}

.vss-calendar.v-calendar.timeframe-day .v-calendar-daily__body {
  display: none !important;
}

.vss-calendar .v-calendar-weekly__day {
  max-height: 140px !important;
  overflow-y: scroll !important;
  overflow-x: hidden !important;
}

.vss-calendar .v-calendar-weekly__day::-webkit-scrollbar {
  width: 0 !important;
}
.vss-calendar .v-calendar-weekly__day {
  overflow: -moz-scrollbars-none !important;
}
.vss-calendar .v-calendar-weekly__day {
  -ms-overflow-style: none !important;
}

.shift-overflow-count .v-chip__content {
  cursor: pointer !important;
}

.shift-details-icon {
  cursor: default;
}
</style>
